/**
 * Who may edit and delete an Additional Information comment they did not write.
 *
 * The section's own edit permission (the `mode` prop, backed by RoleSectionPermission)
 * decides whether the section is writable at all. This is the second, narrower gate on
 * top of it: with the section writable, everyone can still only touch their own comment,
 * except the roles below, who moderate everybody's.
 *
 * There is no matching server-side ownership check - kot-apis `bulk-update` only enforces
 * section edit permission - so this is the whole rule. The audit trail is unaffected:
 * bulk-update stamps `changedByUserId` from the authenticated user, so a moderated edit
 * records the moderator while the row keeps its original author in `userId`.
 */

// KoT role ids from kot-apis `config/rbacMatrix.js`. Note the id for US Patent Paralegal
// is US_PARALEGAL - the display name is not the id - so both spellings are listed and
// normalizeRole() maps the display name onto US_PATENT_PARALEGAL.
const COMMENT_MODERATOR_ROLES = ["ADMIN", "PPS", "FFS", "US_PARALEGAL", "US_PATENT_PARALEGAL"];

const normalizeRole = (role) =>
  String(role || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/**
 * @param roles the current user's KoT roles, i.e. `userRolesMap[userId]` as returned by
 *              `/kot/admin/users/roles`. Absent or empty means no moderation, which is
 *              the pre-existing own-comments-only behaviour.
 */
export const canModerateAdditionalInfoComments = (roles) => (Array.isArray(roles) ? roles : []).some((role) => COMMENT_MODERATOR_ROLES.includes(normalizeRole(role)));
